import { toKstDateString } from '@/shared/lib/utils/kst-calendar';

import type { KimchiPremiumDaily, KimchiPremiumDailyGranularity } from '../types';

/**
 * TRAD-3709 P2 — 김치프리미엄 «일간» 페이지(`/kimchi-premium/YYYY-MM-DD`) 순수 로직. backend TRAD-3719
 * `GET /api/v1/kimchi-premium/daily/{date}?exchange=UPBIT&top=20`(dev #1336, 2026-10-16 12:36 live 대조 — 계약과 불일치 0).
 *
 * 규칙(팀장 GO 2026-10-16):
 * - top 은 한 값으로 고정(서버 캐시 키에 top 이 들어간다) · 거래소는 UPBIT 만(파라미터는 열어 둠).
 * - 당일은 partial(집계 중) — 런타임 전용 + noindex. prerender·sitemap 은 어제까지(scripts/lib/kimchi-day-routes.mjs).
 * - 데이터 없는 날 = 404 KIMCHI_PREMIUM_004 → 빈 상태 + noindex(3726 선례) · 날짜 형식 오류 = 400 E_002 → 화면은 NotFound 로 먼저 가른다.
 * - 6h·1d 시대(≈8일·≈35일 이전)의 closeAt/asOf 는 버킷 시작 시각(18:00·00:00)이라 화면엔 날짜만, 시각은 granularity 5m 일 때만.
 */

export const KIMCHI_DAILY_EXCHANGE = 'UPBIT';
export const KIMCHI_DAILY_TOP = 20;
/** 데이터 없는 날의 ErrorCode(backend KimchiPremiumErrorCode). */
export const KIMCHI_DAILY_NOT_FOUND_CODE = 'KIMCHI_PREMIUM_004';

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` 이고 달력에 실제로 있는 날짜인가(2026-02-30 거절). 캘린더 일자 페이지(day-page.ts)와 같은 규칙. */
export const isValidKimchiDay = (value: string | undefined | null): value is string => {
  if (!value) return false;
  const m = DAY_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
};

/** 날짜만 더하고 뺀다(UTC 산술 — 시간대·서머타임 무관). */
export const shiftKimchiDay = (day: string, deltaDays: number): string => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + deltaDays)).toISOString().slice(0, 10);
};

export const kimchiDayPath = (day: string): string => `/kimchi-premium/${day}`;

/** KST 기준 «오늘» `YYYY-MM-DD`(빌드 서버·브라우저 시간대 무관). */
export const kimchiKstToday = (now: Date = new Date()): string =>
  toKstDateString(now) ?? now.toISOString().slice(0, 10);

/**
 * 색인 여부 — «집계가 끝난 날의 데이터가 있을 때»만. 모르면(로딩·실패·404) 색인하지 않는다(보수적, 3726 결정 B 와 같은 축).
 * 당일(partial=true)은 하루 종일 수치가 바뀌므로 색인하지 않는다(팀장 보강 ①: 낡은 스냅샷이 인덱스에 남는 것을 막는다).
 */
export const isKimchiDayIndexable = (data: KimchiPremiumDaily | undefined | null): boolean =>
  !!data && data.partial === false && (data.top.length > 0 || data.pinned.length > 0);

/**
 * 색인 금지 판정(2026-10-27 팀장 결정 — derivatives soft 404 정정과 같은 원칙).
 *
 * 창 밖 날짜는 robots 없는 셸로 나가 렌더된 PageMeta 가 색인을 정한다(00c571fa). 종전엔 `!isKimchiDayIndexable(data)` 라
 * **로딩·오류 동안 noindex** 를 냈다가 데이터가 오면 index 로 바꿨다 — Google 은 «JS 로 noindex → index» 를 믿지 말라고 하고,
 * Googlebot 렌더 시점에 API 가 느리거나 오류면 실제 데이터가 있는 날이 색인에서 빠진다.
 * 그래서 noindex 는 «데이터 없이도 확정되는 것»만:
 * - 오늘·미래(KST) — 오늘은 집계 중(partial)이라 낡은 수치가 색인된다, 미래는 서버가 404 다. 날짜만으로 안다
 * - 404 KIMCHI_PREMIUM_004 확정 — 그날 데이터가 없다
 * - 데이터가 왔는데 색인 조건 밖(partial · 빈 집계)
 * 로딩·500·네트워크 오류는 noindex 를 내지 않는다(종전 기본 index, follow).
 * 날짜는 둘 다 `YYYY-MM-DD`(KST) 라 문자열 비교가 곧 날짜 비교다.
 */
export const decideKimchiDayNoIndex = ({
  day,
  today,
  data,
  notFound,
}: {
  day: string;
  today: string;
  data: KimchiPremiumDaily | undefined | null;
  notFound: boolean;
}): boolean => {
  if (day >= today) return true;
  if (notFound) return true;
  if (data) return !isKimchiDayIndexable(data);
  return false;
};

/**
 * closeAt/asOf 표시 — granularity 5m 일 때만 시각(`YYYY-MM-DD HH:mm`), 1h·6h·1d 는 날짜만(버킷 시작 시각이라 «시각»이 아니다).
 * 서버 값은 `+09:00` 고정 ISO 라 문자열 슬라이스가 곧 KST 다 — Intl 을 안 써서 Node 22/25·브라우저 로케일에 따라 안 갈린다.
 */
export const formatKimchiDailyInstant = (
  iso: string | null | undefined,
  granularity: KimchiPremiumDailyGranularity | string | null | undefined,
): string => {
  if (!iso || iso.length < 10) return '';
  const day = iso.slice(0, 10);
  if (granularity !== '5m' || iso.length < 16) return day;
  return `${day} ${iso.slice(11, 16)}`;
};

/** 응답의 대표 해상도 — pinned 첫 코인 → top 첫 코인 → 없으면 '1d'(가장 거친 쪽 = 시각을 안 보인다). */
export const representativeGranularity = (
  data: Pick<KimchiPremiumDaily, 'pinned' | 'top'>,
): KimchiPremiumDailyGranularity | string => data.pinned[0]?.granularity ?? data.top[0]?.granularity ?? '1d';
